import { StorageWorkerClient } from './storage_worker_client.js'
import { createStore } from 'tinybase'
import { createIndexedDbPersister } from 'tinybase/persisters/persister-indexed-db'
import { DirectUpload } from '@rails/activestorage'

// Sync status constants
export const SYNC_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed'
}

class SubjectStore {
  constructor() {
    this.storageWorkerClient = new StorageWorkerClient()

    // Initialize TinyBase store for all subject data
    this.store = createStore().setTables({
      subjects: {},      // Store subject metadata with sync status
      photos: {},        // Store photo metadata (subjectId as key prefix)
      audio: {},         // Store audio metadata (subjectId as key)
    })

    // Create persister to save to IndexedDB
    this.persister = createIndexedDbPersister(this.store, 'StandardVisionDB')

    // Track initialization state
    this.isInitialized = false
    this.isFirstInit = !window._subjectStoreInitialized
    this.initializationPromise = this.#initializePersistence()
  }

  async #initializePersistence() {
    try {
      await this.persister.startAutoLoad()
      await this.persister.startAutoSave()

      // Only reset stuck syncing states on first initialization (page reload)
      // This preserves ongoing syncs during navigation within the app
      if (this.isFirstInit) {
        this.#resetStuckSyncingStates()
        window._subjectStoreInitialized = true
      }

      this.isInitialized = true
    } catch (error) {
      console.warn('Failed to initialize persistence:', error)
      this.isInitialized = true // Still mark as initialized to prevent hanging
    }
  }

  // Ensure persistence is ready before operations
  async #waitForInitialization() {
    if (!this.isInitialized) {
      await this.initializationPromise
    }
  }

  // Public method to wait for initialization (useful for external components)
  async waitForReady() {
    await this.#waitForInitialization()
  }

  async save({ id, timestamp, photos, audio, projectId, projectSpecificationIds = [] }) {
    // Wait for persistence to be ready
    await this.#waitForInitialization()

    const subjectId = id || this.#generateSubjectId()
    const createdAt = timestamp || new Date().toISOString()

    // Store files in OPFS
    const photoFileHandles = await this.#storePhotosInOPFS(subjectId, photos)
    let audioFileHandle = null

    if (audio) {
      audioFileHandle = await this.#storeAudioInOPFS(subjectId, audio)
    }

    // Store photos metadata in separate table
    photoFileHandles.forEach((handle, index) => {
      const photoId = `${subjectId}_photo_${index}`
      this.store.setRow('photos', photoId, {
        subjectId,
        name: handle.name,
        size: handle.size,
        type: handle.type,
        index: index
      })
    })

    // Store audio metadata in separate table if present
    if (audioFileHandle) {
      this.store.setRow('audio', subjectId, {
        subjectId,
        name: audioFileHandle.name,
        size: audioFileHandle.size,
        type: audioFileHandle.type
      })
    }

    // Store subject data in TinyBase (primitives only)
    const subjectData = {
      id: subjectId,
      projectId,
      projectSpecificationIds: JSON.stringify(projectSpecificationIds),
      photoCount: photoFileHandles.length,
      hasAudio: !!audioFileHandle,
      createdAt,
      syncStatus: SYNC_STATUS.PENDING,
      progress: 0,
      startedAt: null,
      completedAt: null,
      error: null,
      retryCount: 0,
    }

    this.store.setRow('subjects', subjectId, subjectData)

    return subjectId
  }

  async getAll(projectId) {
    try {
      // Wait for persistence to be ready
      await this.#waitForInitialization()

      // Get subjects from TinyBase filtered by projectId
      const subjectsTable = this.store.getTable('subjects')
      const photosTable = this.store.getTable('photos')
      const audioTable = this.store.getTable('audio')

      const subjects = Object.values(subjectsTable).filter(subject =>
        subject.projectId === projectId
      )

      // Reconstruct subjects with photos and audio arrays
      const reconstructedSubjects = subjects.map(subject => {
        // Get photos for this subject
        const subjectPhotos = Object.values(photosTable)
          .filter(photo => photo.subjectId === subject.id)
          .sort((a, b) => a.index - b.index) // Sort by original order
          .map(photo => ({
            name: photo.name,
            size: photo.size,
            type: photo.type
          }))

        // Get audio for this subject
        const subjectAudio = audioTable[subject.id] ? {
          name: audioTable[subject.id].name,
          size: audioTable[subject.id].size,
          type: audioTable[subject.id].type
        } : null

        return {
          ...subject,
          projectSpecificationIds: JSON.parse(subject.projectSpecificationIds || '[]'),
          hasAudio: !!subjectAudio,
          photosAsync: this.#getPhotoDataUrls(subject.id),
        }
      })

      return reconstructedSubjects
    } catch (error) {
      console.error('Error getting subjects:', error)
      return []
    }
  }

  async getById(subjectId) {
    // Wait for persistence to be ready
    await this.#waitForInitialization()

    const subject = this.store.getRow('subjects', subjectId)
    if (!subject) return null

    // Get photos and audio from separate tables
    const photosTable = this.store.getTable('photos')
    const audioTable = this.store.getTable('audio')

    const subjectPhotos = Object.values(photosTable)
      .filter(photo => photo.subjectId === subjectId)
      .sort((a, b) => a.index - b.index)
      .map(photo => ({
        name: photo.name,
        size: photo.size,
        type: photo.type
      }))

    const subjectAudio = audioTable[subjectId] ? {
      name: audioTable[subjectId].name,
      size: audioTable[subjectId].size,
      type: audioTable[subjectId].type
    } : null

    return {
      ...subject,
      projectSpecificationIds: JSON.parse(subject.projectSpecificationIds || '[]'),
      hasAudio: !!subjectAudio,
      photosAsync: this.#getPhotoDataUrls(subjectId),
    }
  }

  async delete(subjectId) {
    // Wait for initialization to ensure store is ready
    await this.#waitForInitialization()

    try {
      // Always remove from TinyBase tables first to ensure immediate UI updates
      // Remove from subjects table
      this.store.delRow('subjects', subjectId)
      console.log(`Removed subject ${subjectId} from subjects table`)

      // Remove associated photos
      const photosTable = this.store.getTable('photos')
      let deletedPhotos = 0
      Object.keys(photosTable).forEach(photoId => {
        if (photosTable[photoId].subjectId === subjectId) {
          this.store.delRow('photos', photoId)
          deletedPhotos++
        }
      })
      console.log(`Removed ${deletedPhotos} photos for subject ${subjectId}`)

      // Remove associated audio
      this.store.delRow('audio', subjectId)
      console.log(`Removed audio for subject ${subjectId}`)

      // Then try to delete files from OPFS (works offline)
      try {
        await this.#deleteFromOPFS(subjectId)
        console.log(`Successfully deleted OPFS files for subject ${subjectId}`)
      } catch (opfsError) {
        console.log(`Could not delete OPFS files for subject ${subjectId}:`, opfsError.message)
        // This is not a critical error - the UI should still be updated
      }

      console.log(`Successfully deleted subject ${subjectId}`)
    } catch (error) {
      console.error(`Error deleting subject ${subjectId}:`, error)
      throw error
    }
  }

  async syncToServer(subjectId, onProgress = null) {
    const subject = await this.getById(subjectId)
    if (!subject) return

    try {
      // Start syncing
      const currentSubject = this.store.getRow('subjects', subject.id)
      this.store.setRow('subjects', subject.id, {
        ...currentSubject,
        syncStatus: SYNC_STATUS.IN_PROGRESS,
        startedAt: Date.now(),
        error: null
      })

      // Progress: 10% - Preparing files
      this.#updateProgress(subject.id, 10)
      onProgress?.(10)

      // Get files from OPFS
      const files = await this.#getFilesFromOPFS(subject.id)

      // Progress: 20% - Files loaded, starting uploads
      this.#updateProgress(subject.id, 20)
      onProgress?.(20)

      // Upload files using direct upload
      const uploadedPhotoIds = []
      let uploadedAudioId = null

      // Calculate progress steps - photos take 70% of total, audio takes 10%
      const totalFiles = files.photos.length + (files.audio ? 1 : 0)
      const progressPerFile = 70 / totalFiles

      // Upload photos using direct upload
      for (let i = 0; i < files.photos.length; i++) {
        const photoFile = files.photos[i]

        try {
          const signedId = await this.#uploadFileDirect(photoFile.file, (uploadProgress) => {
            const baseProgress = 20 + (i * progressPerFile)
            const fileProgress = (uploadProgress / 100) * progressPerFile
            const totalProgress = Math.min(baseProgress + fileProgress, 90)
            this.#updateProgress(subject.id, totalProgress)
            onProgress?.(totalProgress)
          })

          uploadedPhotoIds.push(signedId)
          console.log(`Uploaded photo ${photoFile.name} with id ${signedId}`)
        } catch (uploadError) {
          console.error(`Failed to upload photo ${photoFile.name}:`, uploadError)
          throw new Error(`Photo upload failed: ${uploadError.message}`)
        }
      }

      // Upload audio if present
      if (files.audio) {
        try {
          uploadedAudioId = await this.#uploadFileDirect(files.audio.file, (uploadProgress) => {
            const baseProgress = 20 + (files.photos.length * progressPerFile)
            const fileProgress = (uploadProgress / 100) * 10
            const totalProgress = Math.min(baseProgress + fileProgress, 90)
            this.#updateProgress(subject.id, totalProgress)
            onProgress?.(totalProgress)
          })
        } catch (uploadError) {
          console.error(`Failed to upload audio ${files.audio.name}:`, uploadError)
          throw new Error(`Audio upload failed: ${uploadError.message}`)
        }
      }

      // Progress: 90% - Creating subject record
      this.#updateProgress(subject.id, 90)
      onProgress?.(90)

      // Create subject record with uploaded file IDs
      const formData = new FormData()

      // Add uploaded photo IDs
      uploadedPhotoIds.forEach(signedId => {
        formData.append('subject_form[submitted_files][]', signedId)
      })

      // Add uploaded audio ID if present
      if (uploadedAudioId) {
        formData.append('subject_form[submitted_files][]', uploadedAudioId)
      }

      // Add project specification IDs if present
      subject.projectSpecificationIds.forEach(specId => {
        formData.append('subject_form[project_specification_ids][]', specId)
      })

      // Submit to server
      const response = await fetch(`/projects/${subject.projectId}/subjects`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      // Progress: 100% - Complete
      this.#updateProgress(subject.id, 100)
      onProgress?.(100)

      const completedSubject = this.store.getRow('subjects', subject.id)
      this.store.setRow('subjects', subject.id, {
        ...completedSubject,
        syncStatus: SYNC_STATUS.COMPLETED,
        completedAt: Date.now(),
        progress: 100
      })

      // Clean up after fade-out completes (3 seconds total)
      setTimeout(() => {
        this.delete(subjectId)
      }, 3000)

    } catch (error) {
      // Check if we went offline during sync
      if (!navigator.onLine) {
        // Reset to pending if we went offline
        const offlineSubject = this.store.getRow('subjects', subject.id)
        this.store.setRow('subjects', subject.id, {
          ...offlineSubject,
          syncStatus: SYNC_STATUS.PENDING,
          progress: 0
        })
        throw error
      }

      // Mark as failed only for actual server/network errors
      const failedSubject = this.store.getRow('subjects', subject.id)
      this.store.setRow('subjects', subject.id, {
        ...failedSubject,
        syncStatus: SYNC_STATUS.FAILED,
        error: error.message,
        retryCount: (failedSubject?.retryCount || 0) + 1
      })
      throw error
    }
  }

  // ===== SYNC STATUS METHODS =====

  getSyncStatus(subjectId) {
    const subject = this.store.getRow('subjects', subjectId)
    if (!subject) return null

    return {
      status: subject.syncStatus,
      progress: subject.progress,
      startedAt: subject.startedAt,
      completedAt: subject.completedAt,
      error: subject.error,
      retryCount: subject.retryCount
    }
  }

  getStore() {
    return this.store
  }

  clearSyncQueue() {
    this.store.setTables({
      subjects: {},      // Clear subjects
      photos: {},        // Clear photos
      audio: {},         // Clear audio
    })
  }

  async syncAllPending(projectId, onProgress = null) {
    const subjects = await this.getAll(projectId)
    const pendingSubjects = subjects.filter(subject => {
      return !subject.syncStatus ||
             subject.syncStatus === SYNC_STATUS.PENDING ||
             subject.syncStatus === SYNC_STATUS.FAILED
    })

    for (const subject of pendingSubjects) {
      try {
        await this.syncToServer(subject.id, onProgress)
        // Small delay between syncs to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 500))
      } catch (error) {
        console.error(`Failed to sync subject ${subject.id}:`, error)
        throw error
        // Continue with other subjects even if one fails
      }
    }
  }

  // ===== PRIVATE METHODS =====

  #resetStuckSyncingStates() {
    // Reset subjects that are stuck in IN_PROGRESS state
    // This happens when the page is refreshed during sync
    const subjectsTable = this.store.getTable('subjects')

    Object.entries(subjectsTable).forEach(([subjectId, subject]) => {
      if (subject.syncStatus === SYNC_STATUS.IN_PROGRESS) {
        this.store.setRow('subjects', subjectId, {
          ...subject,
          syncStatus: SYNC_STATUS.PENDING,
          progress: 0,
          startedAt: null,
          error: null
        })
        console.log(`Reset subject ${subjectId} from IN_PROGRESS to PENDING on page load`)
      }
    })
  }

  #generateSubjectId() {
    return 'subject_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15)
  }

  #getFileExtension(filename) {
    return filename.split('.').pop().toLowerCase() || 'jpg'
  }

  async #storePhotosInOPFS(subjectId, photos) {
    const fileHandles = []
    const opfsRoot = await navigator.storage.getDirectory()
    const subjectsDir = await opfsRoot.getDirectoryHandle('subjects', { create: true })
    const subjectDir = await subjectsDir.getDirectoryHandle(subjectId, { create: true })

    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i]
      const fileName = `photo_${i + 1}_${Date.now()}.${this.#getFileExtension(photo.name)}`
      const fileHandle = await subjectDir.getFileHandle(fileName, { create: true })

      if (fileHandle.createWritable) {
        // Use standard createWritable when available
        const writable = await fileHandle.createWritable()
        await writable.write(photo)
        await writable.close()

        fileHandles.push({
          name: fileName,
          size: photo.size,
          type: photo.type,
          handle: fileHandle
        })
      } else {
        // Fall back to web worker when createWritable is not available
        const result = await this.storageWorkerClient.storeFile(subjectId, fileName, photo)
        fileHandles.push({
          name: fileName,
          size: result.size || photo.size,
          type: photo.type
        })
      }
    }

    return fileHandles
  }

  async #storeAudioInOPFS(subjectId, audioBlob) {
    const fileName = `audio_note_${Date.now()}.mp3`
    const opfsRoot = await navigator.storage.getDirectory()
    const subjectsDir = await opfsRoot.getDirectoryHandle('subjects', { create: true })
    const subjectDir = await subjectsDir.getDirectoryHandle(subjectId, { create: true })
    const fileHandle = await subjectDir.getFileHandle(fileName, { create: true })

    if (fileHandle.createWritable) {
      // Use standard createWritable when available
      const writable = await fileHandle.createWritable()
      await writable.write(audioBlob)
      await writable.close()

      return {
        name: fileName,
        size: audioBlob.size,
        type: audioBlob.type,
        handle: fileHandle
      }
    } else {
      // Fall back to web worker when createWritable is not available
      const result = await this.storageWorkerClient.storeFile(subjectId, fileName, audioBlob)
      return {
        name: fileName,
        size: result.size || audioBlob.size,
        type: audioBlob.type
      }
    }
  }


  async #deleteFromOPFS(subjectId) {
    try {
      const opfsRoot = await navigator.storage.getDirectory()
      const subjectsDir = await opfsRoot.getDirectoryHandle('subjects')
      await subjectsDir.removeEntry(subjectId, { recursive: true })
    } catch (error) {
      console.log('Subject directory not found in OPFS, skipping deletion')
    }
  }

  async #getFilesFromOPFS(subjectId) {
    const opfsRoot = await navigator.storage.getDirectory()
    const subjectsDir = await opfsRoot.getDirectoryHandle('subjects')
    const subjectDir = await subjectsDir.getDirectoryHandle(subjectId)

    const files = {
      photos: [],
      audio: null
    }

    for await (const [name, handle] of subjectDir.entries()) {
      if (handle.kind === 'file') {
        const file = await handle.getFile()
        if (name.startsWith('photo_')) {
          files.photos.push({ file, name })
        } else if (name.startsWith('audio_')) {
          files.audio = { file, name }
        }
      }
    }

    return files
  }

  async #getPhotoDataUrls(subjectId) {
    try {
      const opfsRoot = await navigator.storage.getDirectory()
      const subjectsDir = await opfsRoot.getDirectoryHandle('subjects')
      const subjectDir = await subjectsDir.getDirectoryHandle(subjectId)

      const photoDataUrls = []

      for await (const [name, handle] of subjectDir.entries()) {
        if (handle.kind === 'file' && name.startsWith('photo_')) {
          const file = await handle.getFile()
          const dataUrl = await this.#fileToDataUrl(file)
          photoDataUrls.push({
            name,
            dataUrl,
            size: file.size,
            type: file.type
          })
        }
      }

      return photoDataUrls
    } catch (error) {
      console.error('Error getting photo data URLs:', error)
      return []
    }
  }

  #fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.onerror = (e) => reject(e)
      reader.readAsDataURL(file)
    })
  }

  #updateProgress(subjectId, progress) {
    const currentSubject = this.store.getRow('subjects', subjectId)
    if (currentSubject?.syncStatus === SYNC_STATUS.IN_PROGRESS) {
      this.store.setRow('subjects', subjectId, {
        ...currentSubject,
        progress: Math.max(0, Math.min(100, progress))
      })
    }
  }

  async #uploadFileDirect(file, onProgress = null) {
    return new Promise((resolve, reject) => {
      const directUploadUrl = '/rails/active_storage/direct_uploads'

      const upload = new DirectUpload(file, directUploadUrl, {
        directUploadWillStoreFileWithXHR: (xhr) => {
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && onProgress) {
              const percentage = Math.round((event.loaded / event.total) * 100)
              onProgress(percentage)
            }
          })
        }
      })

      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          resolve(blob.signed_id)
        }
      })
    })
  }
}

export const subjectStore = new SubjectStore()